/**
 * Data Merging Logic for Campaign Tracking System
 * Provides efficient utilities for merging new API data with existing cached data
 */

import type { 
  EnhancedMessageTrackingData, 
  MessageTrackingData, 
  CampaignStats,
  MailgunEvent 
} from '../services/outreach/types';

// Configuration for merge operations
const MERGE_CONFIG = {
  // Maximum age difference (in ms) to consider events as potentially duplicate
  DUPLICATE_THRESHOLD: 5000, // 5 seconds
  // Maximum number of events to process in a single merge operation
  MAX_EVENTS_PER_MERGE: 1000,
  // Fields that should trigger a full data refresh if changed
  CRITICAL_FIELDS: ['status', 'mailgunMessageId', 'recipient'] as const,
} as const;

// Types for merge operations
export interface MergeResult<T> {
  data: T;
  hasChanges: boolean;
  changesSummary: {
    added: number;
    updated: number;
    removed: number;
    conflicts: number;
  };
  conflicts?: Array<{
    field: string;
    oldValue: any;
    newValue: any;
    resolution: 'kept_old' | 'used_new' | 'merged' | 'custom';
  }>;
}

export interface MergeOptions {
  /** Strategy for handling timestamp conflicts */
  timestampStrategy: 'latest' | 'earliest' | 'preserve_existing';
  /** Whether to validate data integrity after merge */
  validateIntegrity: boolean;
  /** Maximum number of items to process in one operation */
  maxItems?: number;
  /** Whether to deep merge nested objects */
  deepMerge: boolean;
  /** Custom conflict resolution function */
  conflictResolver?: (field: string, oldValue: any, newValue: any) => any;
}

// Default merge options
const DEFAULT_MERGE_OPTIONS: MergeOptions = {
  timestampStrategy: 'latest',
  validateIntegrity: true,
  maxItems: MERGE_CONFIG.MAX_EVENTS_PER_MERGE,
  deepMerge: true,
};

/**
 * Deep comparison utility to detect meaningful changes between objects
 * Ignores timestamp differences within the duplicate threshold
 */
export const hasSignificantChanges = (
  obj1: any, 
  obj2: any, 
  ignoredFields: string[] = ['timestamp', 'lastUpdated']
): boolean => {
  if (obj1 === obj2) return false;
  if (typeof obj1 !== typeof obj2) return true;
  if (obj1 === null || obj2 === null) return obj1 !== obj2;
  if (typeof obj1 !== 'object') return obj1 !== obj2;

  const keys1 = Object.keys(obj1).filter(key => !ignoredFields.includes(key));
  const keys2 = Object.keys(obj2).filter(key => !ignoredFields.includes(key));

  if (keys1.length !== keys2.length) return true;

  for (const key of keys1) {
    if (!keys2.includes(key)) return true;
    
    // Special handling for timestamps - check if difference is significant
    if (key.toLowerCase().includes('timestamp') || key.toLowerCase().includes('at')) {
      const time1 = new Date(obj1[key]).getTime();
      const time2 = new Date(obj2[key]).getTime();
      if (Math.abs(time1 - time2) > MERGE_CONFIG.DUPLICATE_THRESHOLD) return true;
    } else if (hasSignificantChanges(obj1[key], obj2[key], ignoredFields)) {
      return true;
    }
  }

  return false;
};

/**
 * Merge two objects with conflict resolution
 */
const mergeObjects = <T extends Record<string, any>>(
  existing: T,
  incoming: T,
  options: MergeOptions
): { merged: T; conflicts: Array<{ field: string; oldValue: any; newValue: any; resolution: 'kept_old' | 'used_new' | 'merged' | 'custom' }> } => {
  const merged = { ...existing };
  const conflicts: Array<{ field: string; oldValue: any; newValue: any; resolution: 'kept_old' | 'used_new' | 'merged' | 'custom' }> = [];

  for (const [key, newValue] of Object.entries(incoming)) {
    const oldValue = existing[key];

    if (oldValue === undefined) {
      // New field, add it
      (merged as any)[key] = newValue;
    } else if (oldValue !== newValue) {
      // Conflict detected
      let resolvedValue = newValue;
      let resolution: 'kept_old' | 'used_new' | 'merged' | 'custom' = 'used_new';

      // Apply timestamp strategy for timestamp fields
      if (key.toLowerCase().includes('timestamp') || key.toLowerCase().includes('at')) {
        const oldTime = new Date(oldValue).getTime();
        const newTime = new Date(newValue).getTime();

        switch (options.timestampStrategy) {
          case 'latest':
            resolvedValue = newTime > oldTime ? newValue : oldValue;
            resolution = newTime > oldTime ? 'used_new' : 'kept_old';
            break;
          case 'earliest':
            resolvedValue = newTime < oldTime ? newValue : oldValue;
            resolution = newTime < oldTime ? 'used_new' : 'kept_old';
            break;
          case 'preserve_existing':
            resolvedValue = oldValue;
            resolution = 'kept_old';
            break;
        }
      } else if (options.deepMerge && typeof oldValue === 'object' && typeof newValue === 'object') {
        // Deep merge objects
        const deepMergeResult = mergeObjects(oldValue, newValue, options);
        resolvedValue = deepMergeResult.merged;
        conflicts.push(...deepMergeResult.conflicts.map(c => ({ 
          ...c, 
          field: `${key}.${c.field}`,
          resolution: c.resolution as 'kept_old' | 'used_new' | 'merged' | 'custom'
        })));
        resolution = 'merged';
      } else if (options.conflictResolver) {
        // Use custom conflict resolver
        resolvedValue = options.conflictResolver(key, oldValue, newValue);
        resolution = 'custom';
      }

      (merged as any)[key] = resolvedValue;
      conflicts.push({ field: key, oldValue, newValue, resolution });
    }
  }

  return { merged, conflicts };
};

/**
 * Merge tracking events by message ID with intelligent conflict resolution
 */
export const mergeTrackingEvents = (
  existingData: EnhancedMessageTrackingData[],
  newData: EnhancedMessageTrackingData[],
  options: Partial<MergeOptions> = {}
): MergeResult<EnhancedMessageTrackingData[]> => {
  const mergeOptions = { ...DEFAULT_MERGE_OPTIONS, ...options };
  const result: EnhancedMessageTrackingData[] = [];
  const changesSummary = { added: 0, updated: 0, removed: 0, conflicts: 0 };
  const allConflicts: Array<{ field: string; oldValue: any; newValue: any; resolution: 'kept_old' | 'used_new' | 'merged' | 'custom' }> = [];

  // Create maps for efficient lookup
  const existingMap = new Map<string, EnhancedMessageTrackingData>();
  const newMap = new Map<string, EnhancedMessageTrackingData>();

  // Index existing data by message ID
  existingData.forEach(item => {
    const key = item.mailgunMessageId || item.id;
    existingMap.set(key, item);
  });

  // Index new data by message ID
  newData.forEach(item => {
    const key = item.mailgunMessageId || item.id;
    newMap.set(key, item);
  });

  // Process existing items
  for (const [messageId, existingItem] of Array.from(existingMap)) {
    const newItem = newMap.get(messageId);

    if (!newItem) {
      // Item exists only in existing data - keep it
      result.push(existingItem);
    } else {
      // Item exists in both - merge them
      if (hasSignificantChanges(existingItem, newItem)) {
        const mergeResult = mergeObjects(existingItem, newItem, mergeOptions);
        result.push(mergeResult.merged);
        
        if (mergeResult.conflicts.length > 0) {
          allConflicts.push(...mergeResult.conflicts);
          changesSummary.conflicts += mergeResult.conflicts.length;
        }
        changesSummary.updated++;
      } else {
        // No significant changes, keep existing
        result.push(existingItem);
      }
      
      // Remove from new map to track what's been processed
      newMap.delete(messageId);
    }
  }

  // Add new items that weren't in existing data
  for (const [, newItem] of Array.from(newMap)) {
    result.push(newItem);
    changesSummary.added++;
  }

  // Sort by timestamp (most recent first)
  result.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  // Validate integrity if requested
  if (mergeOptions.validateIntegrity) {
    const validationErrors = validateTrackingDataIntegrity(result);
    if (validationErrors.length > 0) {
      console.warn('Data integrity issues detected after merge:', validationErrors);
    }
  }

  return {
    data: result,
    hasChanges: changesSummary.added > 0 || changesSummary.updated > 0 || changesSummary.removed > 0,
    changesSummary,
    conflicts: allConflicts.length > 0 ? allConflicts : undefined,
  };
};

/**
 * Merge campaign statistics with conflict resolution
 */
export const mergeCampaignStats = (
  existingStats: CampaignStats | null,
  newStats: CampaignStats,
  options: Partial<MergeOptions> = {}
): MergeResult<CampaignStats> => {
  const mergeOptions = { ...DEFAULT_MERGE_OPTIONS, ...options };

  if (!existingStats) {
    return {
      data: newStats,
      hasChanges: true,
      changesSummary: { added: 1, updated: 0, removed: 0, conflicts: 0 },
    };
  }

  const mergeResult = mergeObjects(existingStats, newStats, mergeOptions);
  
  return {
    data: mergeResult.merged,
    hasChanges: hasSignificantChanges(existingStats, newStats),
    changesSummary: { 
      added: 0, 
      updated: hasSignificantChanges(existingStats, newStats) ? 1 : 0, 
      removed: 0, 
      conflicts: mergeResult.conflicts.length 
    },
    conflicts: mergeResult.conflicts.length > 0 ? mergeResult.conflicts : undefined,
  };
};

/**
 * Merge partial data updates efficiently
 * Useful for updating specific fields without full data replacement
 */
export const mergePartialUpdate = <T extends Record<string, any>>(
  existingData: T[],
  partialUpdates: Array<{ id: string; updates: Partial<T> }>,
  idField: keyof T = 'id' as keyof T
): MergeResult<T[]> => {
  const result = [...existingData];
  const changesSummary = { added: 0, updated: 0, removed: 0, conflicts: 0 };

  for (const { id, updates } of partialUpdates) {
    const index = result.findIndex(item => item[idField] === id);
    
    if (index !== -1) {
      const existing = result[index];
      const hasChanges = Object.keys(updates).some(key => 
        hasSignificantChanges(existing[key], updates[key])
      );

      if (hasChanges) {
        result[index] = { ...existing, ...updates };
        changesSummary.updated++;
      }
    } else {
      // If item doesn't exist and we have enough data to create it, add it
      if (updates.id || updates[idField]) {
        result.push(updates as T);
        changesSummary.added++;
      }
    }
  }

  return {
    data: result,
    hasChanges: changesSummary.added > 0 || changesSummary.updated > 0,
    changesSummary,
  };
};

/**
 * Validate data integrity after merge operations
 */
export const validateTrackingDataIntegrity = (
  data: EnhancedMessageTrackingData[]
): string[] => {
  const errors: string[] = [];
  const seenIds = new Set<string>();

  for (const item of data) {
    // Check for duplicate IDs
    const id = item.mailgunMessageId || item.id;
    if (seenIds.has(id)) {
      errors.push(`Duplicate message ID found: ${id}`);
    }
    seenIds.add(id);

    // Validate required fields
    if (!item.recipient) {
      errors.push(`Missing recipient for message ${id}`);
    }
    if (!item.timestamp) {
      errors.push(`Missing timestamp for message ${id}`);
    }

    // Validate timestamp consistency
    if (item.deliveredAt && item.timestamp) {
      const sentTime = new Date(item.timestamp).getTime();
      const deliveredTime = new Date(item.deliveredAt).getTime();
      if (deliveredTime < sentTime) {
        errors.push(`Delivered time before sent time for message ${id}`);
      }
    }

    // Validate status consistency
    if (item.status === 'delivered' && !item.deliveredAt) {
      errors.push(`Status is 'delivered' but no deliveredAt timestamp for message ${id}`);
    }
    if (item.status === 'opened' && !item.openedAt) {
      errors.push(`Status is 'opened' but no openedAt timestamp for message ${id}`);
    }
  }

  return errors;
};

/**
 * Create a merge strategy optimized for large datasets
 * Uses streaming approach to handle memory efficiently
 */
export const createLargeDatasetMerger = <T extends Record<string, any>>(
  chunkSize: number = 100
) => {
  return async function* mergeLargeDataset(
    existingData: T[],
    newData: T[],
    keyField: keyof T,
    options: Partial<MergeOptions> = {}
  ): AsyncGenerator<MergeResult<T[]>, void, unknown> {
    const mergeOptions = { ...DEFAULT_MERGE_OPTIONS, ...options };
    const totalChunks = Math.ceil(Math.max(existingData.length, newData.length) / chunkSize);

    for (let i = 0; i < totalChunks; i++) {
      const startIndex = i * chunkSize;
      const endIndex = Math.min(startIndex + chunkSize, Math.max(existingData.length, newData.length));

      const existingChunk = existingData.slice(startIndex, endIndex);
      const newChunk = newData.slice(startIndex, endIndex);

      // Process chunk using standard merge logic
      const chunkResult = mergeTrackingEvents(
        existingChunk as any,
        newChunk as any,
        mergeOptions
      );

      yield {
        data: chunkResult.data as unknown as T[],
        hasChanges: chunkResult.hasChanges,
        changesSummary: {
          ...chunkResult.changesSummary,
          // Add progress information
          processed: endIndex,
          total: Math.max(existingData.length, newData.length),
        } as any,
        conflicts: chunkResult.conflicts,
      };
    }
  };
};

/**
 * Utility to detect and resolve timestamp conflicts
 */
export const resolveTimestampConflicts = (
  events: Array<{ timestamp: string; [key: string]: any }>,
  strategy: 'deduplicate' | 'merge' | 'keep_all' = 'deduplicate'
): Array<{ timestamp: string; [key: string]: any }> => {
  if (strategy === 'keep_all') return events;

  const grouped = new Map<string, Array<{ timestamp: string; [key: string]: any }>>();

  // Group events by timestamp (rounded to nearest second to handle minor differences)
  events.forEach(event => {
    const roundedTimestamp = new Date(Math.floor(new Date(event.timestamp).getTime() / 1000) * 1000).toISOString();
    if (!grouped.has(roundedTimestamp)) {
      grouped.set(roundedTimestamp, []);
    }
    grouped.get(roundedTimestamp)!.push(event);
  });

  const result: Array<{ timestamp: string; [key: string]: any }> = [];

  for (const [timestamp, eventGroup] of Array.from(grouped)) {
    if (eventGroup.length === 1) {
      result.push(eventGroup[0]);
    } else if (strategy === 'deduplicate') {
      // Keep the most complete event (most non-null fields)
      const mostComplete = eventGroup.reduce((best, current) => {
        const bestFields = Object.values(best).filter(v => v != null).length;
        const currentFields = Object.values(current).filter(v => v != null).length;
        return currentFields > bestFields ? current : best;
      });
      result.push(mostComplete);
    } else if (strategy === 'merge') {
      // Merge all events with the same timestamp
      const merged = eventGroup.reduce((acc, event) => ({ ...acc, ...event }), { timestamp } as { timestamp: string; [key: string]: any });
      result.push(merged);
    }
  }

  return result.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
};

/**
 * Performance monitoring for merge operations
 */
export const createMergePerformanceMonitor = () => {
  const metrics = {
    totalOperations: 0,
    totalTime: 0,
    averageTime: 0,
    largestDataset: 0,
    conflictsResolved: 0,
  };

  return {
    startOperation: () => {
      return {
        startTime: performance.now(),
        dataSize: 0,
      };
    },

    endOperation: (operation: { startTime: number; dataSize: number }, conflicts: number = 0) => {
      const duration = performance.now() - operation.startTime;
      metrics.totalOperations++;
      metrics.totalTime += duration;
      metrics.averageTime = metrics.totalTime / metrics.totalOperations;
      metrics.largestDataset = Math.max(metrics.largestDataset, operation.dataSize);
      metrics.conflictsResolved += conflicts;

      return {
        duration,
        metrics: { ...metrics },
      };
    },

    getMetrics: () => ({ ...metrics }),
    
    reset: () => {
      Object.assign(metrics, {
        totalOperations: 0,
        totalTime: 0,
        averageTime: 0,
        largestDataset: 0,
        conflictsResolved: 0,
      });
    },
  };
};